// src/pages/TestEditorPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { useTestContext } from '../context/TestContext';
import { getTest } from '../api/tests';
import { updateQuestion } from '../api/questions';
import { Button } from '../components/common/Button';
import { Input, TextArea } from '../components/common/Input';
import { Card } from '../components/common/Card';
import { ParameterEditor } from '../components/editor/ParameterEditor';
import { AlgorithmEditor } from '../components/editor/AlgorithmEditor';
import { fadeIn } from '../utils/animations';
import { useLang } from '../context/LangContext';
import { Question, AnswerOption } from '../types/testData';
import { Parameter } from '../types';

/* ── Цветомер — константы ───────────────────────────────────────── */
// Палитра цветомера: 0 КрО, 1 ЛиЖ, 2 ТрЗ, 3 МаЛ, 4 ТёС, 5 ТёК, 6 АсС, 7 ЧёС
const COLOR_OPTIONS = ['#FA6400', '#FFFF00', '#00FF00', '#FF007C', '#0000CD', '#A0522D', '#808080', '#000000'];

const DEFAULT_IC_TABLE: number[][] = [
  [0.98, 1.00, 0.90, 0.96, 0.76, 0.65, 0.44, 0.27],
  [0.99, 0.97, 0.84, 0.94, 0.75, 0.64, 0.43, 0.26],
  [0.87, 0.83, 0.78, 0.81, 0.74, 0.63, 0.42, 0.25],
  [0.92, 0.93, 0.86, 0.88, 0.73, 0.52, 0.41, 0.23],
  [0.71, 0.70, 0.69, 0.68, 0.60, 0.46, 0.33, 0.14],
  [0.56, 0.54, 0.50, 0.49, 0.46, 0.31, 0.29, 0.13],
  [0.40, 0.39, 0.38, 0.37, 0.17, 0.16, 0.15, 0.12],
  [0.21, 0.20, 0.19, 0.19, 0.11, 0.10, 0.09, 0.08],
];

/* ── Helpers ────────────────────────────────────────────────────── */

let _tempId = -1;
const nextTempId = () => _tempId--;

const recordToParams = (rec: Record<string, string>): Parameter[] =>
  Object.entries(rec).map(([k, v]) => ({ id: k, name: k, value: v }));

const paramsToRecord = (params: Parameter[]): Record<string, string> =>
  Object.fromEntries(params.map((p) => [p.name, p.value]));

const questionToPayload = (q: Question) => ({
  type: q.type,
  content: q.content,
  info: q.info || '',
  time_limit: q.time_limit,
  order: q.order,
  parameters: q.parameters || {},
  media_url: q.media_url,
  is_hidden: q.is_hidden,
  allow_custom_answer: q.allow_custom_answer ?? false,
  font_size: q.font_size ?? null,
  // Клики до смены стимула: не больше числа ОтО; 1/null → одиночный режим.
  clicks_to_advance: q.clicks_to_advance
    ? Math.max(1, Math.min(q.clicks_to_advance, Math.max(1, q.answer_options.length)))
    : null,
  answer_options: q.answer_options.map((o) => ({
    content: o.content,
    coefficient: o.coefficient,
    parameters: o.parameters || {},
    media_url: o.media_url ?? undefined,
  })),
});

/* ── Styled ─────────────────────────────────────────────────────── */

const PageContainer = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing.xxl} ${theme.spacing.lg}`};
  padding-bottom: 80px;
  background-color: ${({ theme }) => theme.colors.background};
  overflow-y: auto;
`;

const PageHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 700;
`;

const RestoreBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 12px 16px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;

  span {
    flex: 1;
    font-size: 0.95rem;
    color: #92400e;
    font-weight: 500;
  }
`;

const FormSection = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};

  label {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const QuestionsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const QuestionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const QuestionCard = styled(Card)`
  position: relative;
`;

const QuestionCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const QuestionTitle = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
`;

const QuestionActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;


const DuplicateRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FontSelect = styled.select`
  padding: 5px 6px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 14px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const SmallInput = styled(Input)`
  width: 70px;
  padding: 6px 8px;
  font-size: 14px;
  text-align: center;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const OptionLetter = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
  margin-top: 8px;
`;

const CoeffInput = styled(Input)`
  width: 100px;
`;

const HintText = styled.p`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 2px;
`;

// Редактор параметров шкалы (семантический дифференциал) в редакторе стимула.
const ScaleParamsBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
`;

const ScaleParamField = styled.div`
  label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textLight};
    margin-bottom: 4px;
  }
`;

// Кнопка сворачивания/разворачивания блока «МЕТА СТИМУЛА».
const CollapsibleMetaToggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  margin-top: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CollapsibleMetaBody = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
`;

// Сворачиваемый блок «МЕТА СТИМУЛА» (высокие поля параметров — scale_* и т.п.).
const CollapsibleMeta: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <CollapsibleMetaToggle onClick={() => setOpen((v) => !v)} type="button">
        <span>{title}</span>
        <span>{open ? '−' : '+'}</span>
      </CollapsibleMetaToggle>
      <CollapsibleMetaBody $open={open}>{children}</CollapsibleMetaBody>
    </div>
  );
};

// Панель настроек шкалы — min/max/step + подписи полюсов.
const ScaleParamsEditor: React.FC<{
  params: Record<string, string>;
  onChange: (key: string, value: string) => void;
}> = ({ params, onChange }) => (
  <ScaleParamsBox>
    <ScaleParamField>
      <label>{'Шкала от'}</label>
      <Input type="number" step="0.5" value={params.scale_min || '-3'} onChange={(e) => onChange('scale_min', e.target.value)} style={{ width: '100%' }} />
    </ScaleParamField>
    <ScaleParamField>
      <label>{'Шкала до'}</label>
      <Input type="number" step="0.5" value={params.scale_max || '3'} onChange={(e) => onChange('scale_max', e.target.value)} style={{ width: '100%' }} />
    </ScaleParamField>
    <ScaleParamField>
      <label>{'Шаг'}</label>
      <Input type="number" step="0.5" min="0.1" value={params.scale_step || '1'} onChange={(e) => onChange('scale_step', e.target.value)} style={{ width: '100%' }} />
    </ScaleParamField>
    <ScaleParamField>
      <label>{'Подпись слева'}</label>
      <Input value={params.scale_left_label || ''} onChange={(e) => onChange('scale_left_label', e.target.value)} placeholder="Спокойный" style={{ width: '100%' }} />
    </ScaleParamField>
    <ScaleParamField>
      <label>{'Подпись справа'}</label>
      <Input value={params.scale_right_label || ''} onChange={(e) => onChange('scale_right_label', e.target.value)} placeholder="Тревожный" style={{ width: '100%' }} />
    </ScaleParamField>
    <ScaleParamField>
      <label>{'Подпись центра'}</label>
      <Input value={params.scale_mid_label || '0'} onChange={(e) => onChange('scale_mid_label', e.target.value)} placeholder="0" style={{ width: '100%' }} />
    </ScaleParamField>
  </ScaleParamsBox>
);

const FixedBottomBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: ${({ theme }) => theme.colors.cardBackground};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: 10px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
`;

const StimNavScrollArea = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 2px 16px;
  margin: 0 8px;
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.border} transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 2px;
  }
`;

type StimSquareProps = { $active: boolean; $hidden: boolean; $dragOver: boolean };

const StimSquare = styled.div<StimSquareProps>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.cardBackground)};
  color: ${({ $active, $hidden, theme }) =>
    $active ? '#fff' : $hidden ? theme.colors.textLight : theme.colors.text};
  opacity: ${({ $hidden, $active }) => ($hidden && !$active ? 0.45 : 1)};
  outline: ${({ $dragOver, theme }) => ($dragOver ? `2px dashed ${theme.colors.primary}` : 'none')};
  outline-offset: 2px;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ScrollTopButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  bottom: 72px;
  right: 24px;
  z-index: 101;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.25s, filter 0.15s;

  &:hover {
    filter: brightness(0.88);
  }
`;

/* ── Toast ── */
const ToastMsg = styled.div`
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  padding: 12px 24px;
  border-radius: 8px;
  background: #333;
  color: #fff;
  font-size: 0.9rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  animation: fadeInUp 0.3s ease-out;
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateX(-50%) translateY(12px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

/* ── Component ──────────────────────────────────────────────────── */

const EditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const { testId } = useParams<{ testId: string }>();
  const { currentTest, setCurrentTest, addTest, updateTest, addQuestion, duplicateQuestion, deleteQuestion } =
    useTestContext();


  const [formData, setFormData] = useState({ title: '', description: '', methodology: '', shuffleRanges: '', colorMode: false, analysisAlgorithm: '', colorLabels: [] as string[], colorPairIndex: null as number[][] | null });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [duplicateCounts, setDuplicateCounts] = useState<Record<number, number>>({});
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'syncing' | 'error'>('idle');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);
  const [dragSquareIdx, setDragSquareIdx] = useState<number | null>(null);
  const [dragOverSquareIdx, setDragOverSquareIdx] = useState<number | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    if (!toastMsg) return;
    const timer = window.setTimeout(() => setToastMsg(''), 3500);
    return () => window.clearTimeout(timer);
  }, [toastMsg]);

  const questionsRef = useRef<Question[]>([]);
  const dirtyIdsRef = useRef<Set<number>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Таймер сброса статуса автосейва «saved/error → idle»: храним один,
  // чтобы при повторном сейве не копились и чистить на unmount.
  const autoSaveStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasUnsavedRef = useRef(false);
  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const navScrollRef = useRef<HTMLDivElement | null>(null);

  const draftKey = testId && testId !== 'new' ? `test_editor_draft_${testId}` : null;

  useEffect(() => { questionsRef.current = questions; }, [questions]);

  // Track active question via scroll position
  useEffect(() => {
    if (questions.length === 0) return;
    const handleScroll = () => {
      const viewportMid = window.innerHeight / 2;
      let bestId: number | null = null;
      let bestDist = Infinity;
      questions.forEach((q) => {
        const el = questionRefs.current[q.id];
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const dist = Math.abs(rect.top + rect.height / 2 - viewportMid);
        if (dist < bestDist) { bestDist = dist; bestId = q.id; }
      });
      if (bestId !== null) setActiveQuestionId(bestId);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [questions]);

  // Show scroll-to-top button when not at top
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll nav bar: keep active square centered
  useEffect(() => {
    if (activeQuestionId === null || !navScrollRef.current) return;
    const idx = questions.findIndex((q) => q.id === activeQuestionId);
    if (idx === -1) return;
    const sqW = 41; // 36px + 5px gap
    const container = navScrollRef.current;
    const targetLeft = idx * sqW - container.clientWidth / 2 + 18;
    container.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
  }, [activeQuestionId, questions]);

  // Cleanup timers on unmount (debounce + автосейв-статус)
  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (autoSaveStatusTimerRef.current) clearTimeout(autoSaveStatusTimerRef.current);
  }, []);

  /* ── LocalStorage helpers ── */

  const saveDraft = (qs: Question[]) => {
    if (!draftKey) return;
    localStorage.setItem(draftKey, JSON.stringify({ questions: qs, timestamp: Date.now() }));
    hasUnsavedRef.current = true;
  };

  const clearDraft = () => {
    if (!draftKey) return;
    localStorage.removeItem(draftKey);
    hasUnsavedRef.current = false;
  };

  /* ── Server sync ── */

  // Сброс «saved/error → idle» одним таймером: повторный сейв отменяет предыдущий.
  const resetAutoSaveStatusTimer = () => {
    if (autoSaveStatusTimerRef.current) clearTimeout(autoSaveStatusTimerRef.current);
    autoSaveStatusTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 3000);
  };

  const syncToServer = async (qs?: Question[]) => {
    const toSync = qs ?? questionsRef.current;
    if (toSync.length === 0) return;
    setAutoSaveStatus('syncing');
    try {
      await Promise.all(toSync.map((q) => updateQuestion(q.id, questionToPayload(q))));
      clearDraft();
      dirtyIdsRef.current.clear();
      setAutoSaveStatus('saved');
      resetAutoSaveStatusTimer();
    } catch {
      setAutoSaveStatus('error');
      resetAutoSaveStatusTimer();
    }
  };

  /* ── Online / offline detection ── */

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      if (hasUnsavedRef.current) {
        await syncToServer();
      }
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []); // eslint-disable-line

  /* ── beforeunload warning ── */

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /* ── Check for saved draft after load ── */

  useEffect(() => {
    if (!loading && draftKey && localStorage.getItem(draftKey)) {
      setShowRestorePrompt(true);
    }
  }, [loading, draftKey]);

  /* ── Auto-save with debounce ── */

  const scheduleAutoSave = (questionId: number) => {
    dirtyIdsRef.current.add(questionId);
    hasUnsavedRef.current = true;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      const ids = Array.from(dirtyIdsRef.current);
      dirtyIdsRef.current.clear();
      if (ids.length === 0) return;

      // Always persist to localStorage first
      saveDraft(questionsRef.current);

      if (!navigator.onLine) return; // offline — localStorage is enough

      setAutoSaveStatus('saving');
      try {
        await Promise.all(
          questionsRef.current
            .filter((q) => ids.includes(q.id))
            .map((q) => updateQuestion(q.id, questionToPayload(q)))
        );
        clearDraft();
        hasUnsavedRef.current = false;
        setAutoSaveStatus('saved');
        resetAutoSaveStatusTimer();
      } catch {
        setAutoSaveStatus('error');
        resetAutoSaveStatusTimer();
      }
    }, 1500);
  };

  /* ── Restore / discard draft ── */

  const handleRestore = async () => {
    if (!draftKey) return;
    const raw = localStorage.getItem(draftKey);
    if (!raw) return;
    const { questions: saved }: { questions: Question[] } = JSON.parse(raw);
    setQuestions(saved);
    questionsRef.current = saved;
    hasUnsavedRef.current = true;
    setShowRestorePrompt(false);
    if (navigator.onLine) await syncToServer(saved);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowRestorePrompt(false);
  };

  /* ── Load test ── */

  useEffect(() => {
    const loadTest = async () => {
      if (!testId || testId === 'new') { setCurrentTest(null); setQuestions([]); setLoading(false); return; }
      setLoading(true);
      try {
        const data = await getTest(Number(testId));
        setCurrentTest(data);
        setFormData({
          title: data.title,
          description: data.description,
          methodology: data.methodology || '',
          shuffleRanges: (data.shuffle_ranges || []).map(([a, b]: [number, number]) => `${a}-${b}`).join(', '),
          colorMode: data.color_mode ?? false,
          analysisAlgorithm: data.analysis_algorithm || '',
          colorLabels: data.color_labels ?? [],
          colorPairIndex: data.color_pair_index ?? null,
        });
        setQuestions(data.questions || []);
      } catch {
        navigate('/tests');
      } finally {
        setLoading(false);
      }
    };
    loadTest();
  }, [testId]); // eslint-disable-line

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Неявный submit формы по Enter закрывал редактор теста и уводил на /tests.
  // Enter в однострочных полях больше не отправляет форму (в <textarea> остаётся переносом строки).
  const preventImplicitSubmit = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    if (tag === 'TEXTAREA' || tag === 'BUTTON') return;
    e.preventDefault();
  };

  /* ── Question helpers ── */

  const updateLocalQuestion = (qId: number, patch: Partial<Question>) => {
    setQuestions((prev) => prev.map((q) => (q.id === qId ? { ...q, ...patch } : q)));
    scheduleAutoSave(qId);
  };

  // Обновление одного параметра стимула (хранятся как Record<string,string>).
  const updateQuestionParam = (qId: number, key: string, value: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId ? { ...q, parameters: { ...(q.parameters || {}), [key]: value } } : q
      )
    );
    scheduleAutoSave(qId);
  };

  const updateLocalOption = (qId: number, optId: number | undefined, patch: Partial<AnswerOption>) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? { ...q, answer_options: q.answer_options.map((o) => (o.id === optId ? { ...o, ...patch } : o)) }
          : q
      )
    );
    scheduleAutoSave(qId);
  };

const addNewQuestion = async () => {
    if (!currentTest) return;
    await addQuestion(currentTest.id, {
      type: 'text',
      content: '',
      time_limit: 30,
      order: 1,
      parameters: {},
      answer_options: formData.colorMode ? [] : [
        { content: '', coefficient: 1, parameters: {} },
        { content: '', coefficient: 0, parameters: {} },
      ],
    });
    const updated = await getTest(currentTest.id);
    setCurrentTest(updated);
    setQuestions(updated.questions || []);
    const last = updated.questions?.[updated.questions.length - 1];
    if (last) {
      setActiveQuestionId(last.id);
      setTimeout(() => questionRefs.current[last.id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    }
  };

  const handleDuplicateQuestion = async (questionId: number) => {
    if (!currentTest) return;
    const count = duplicateCounts[questionId] ?? 1;
    await duplicateQuestion(currentTest.id, questionId, count);
    const updated = await getTest(currentTest.id);
    setCurrentTest(updated);
    setQuestions(updated.questions || []);
  };

  const handleDeleteQuestion = async (questionId: number) => {
    if (!currentTest) return;
    await deleteQuestion(currentTest.id, questionId);
    const updated = await getTest(currentTest.id);
    setCurrentTest(updated);
    setQuestions(updated.questions || []);
    if (activeQuestionId === questionId) {
      setActiveQuestionId(updated.questions?.[0]?.id ?? null);
    }
    // Remove deleted question from localStorage draft to avoid "ghost" restores
    if (draftKey) {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          const filtered = (parsed.questions || []).filter((q: Question) => q.id !== questionId);
          if (filtered.length === 0) {
            localStorage.removeItem(draftKey);
            hasUnsavedRef.current = false;
          } else {
            localStorage.setItem(draftKey, JSON.stringify({ questions: filtered, timestamp: Date.now() }));
          }
        } catch {}
      }
    }
  };

  const addOptionToQuestion = (qId: number) => {
    const tempId = nextTempId();
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? { ...q, answer_options: [...q.answer_options, { id: tempId, content: '', coefficient: 0, parameters: {} }] }
          : q
      )
    );
    scheduleAutoSave(qId);
  };

  const removeOptionFromQuestion = (qId: number, optId: number | undefined) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? { ...q, answer_options: q.answer_options.filter((o) => o.id !== optId) }
          : q
      )
    );
    scheduleAutoSave(qId);
  };

  /* ── Stimulus navigator ── */

  const handleSquareClick = (questionId: number) => {
    setActiveQuestionId(questionId);
    const el = questionRefs.current[questionId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleSquareDragStart = (e: React.DragEvent, idx: number) => {
    setDragSquareIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSquareDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverSquareIdx(idx);
  };

  const handleSquareDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    const fromIdx = dragSquareIdx;
    setDragSquareIdx(null);
    setDragOverSquareIdx(null);
    if (fromIdx === null || fromIdx === targetIdx) return;

    const newQ = [...questionsRef.current];
    const [moved] = newQ.splice(fromIdx, 1);
    newQ.splice(targetIdx, 0, moved);
    const reordered = newQ.map((q, i) => ({ ...q, order: i + 1 }));

    setQuestions(reordered);
    questionsRef.current = reordered;
    hasUnsavedRef.current = true;
    reordered.forEach((q) => dirtyIdsRef.current.add(q.id));
    saveDraft(reordered);

    if (!navigator.onLine) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      const ids = Array.from(dirtyIdsRef.current);
      dirtyIdsRef.current.clear();
      if (ids.length === 0) return;
      setAutoSaveStatus('saving');
      try {
        await Promise.all(
          questionsRef.current.filter((q) => ids.includes(q.id)).map((q) => updateQuestion(q.id, questionToPayload(q)))
        );
        clearDraft();
        hasUnsavedRef.current = false;
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      } catch {
        setAutoSaveStatus('error');
        setTimeout(() => setAutoSaveStatus('idle'), 5000);
      }
    }, 1500);
  };

  const handleSquareDragEnd = () => {
    setDragSquareIdx(null);
    setDragOverSquareIdx(null);
  };

  /* ── Test meta save ── */

  const validateAndParseShuffleRanges = (input: string): { ranges: [number, number][]; error: string | null } => {
    if (!input.trim()) return { ranges: [], error: null };
    const segments = input.split(',').map(s => s.trim()).filter(Boolean);
    const ranges: [number, number][] = [];
    for (const s of segments) {
      const m = s.match(/^(\d+)\s*-\s*(\d+)$/);
      if (!m) return { ranges: [], error: t('editorShuffleErrorFormat') };
      const a = parseInt(m[1]), b = parseInt(m[2]);
      if (b < a) return { ranges: [], error: t('editorShuffleErrorOrder') };
      if (questions.length > 0 && b > questions.length)
        return { ranges: [], error: t('editorShuffleErrorMax').replace('{n}', String(questions.length)) };
      ranges.push([a, b]);
    }
    return { ranges, error: null };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setFormErrors({ title: t('editorErrorName') });
      return;
    }
    const { ranges: shuffleRangesParsed, error: shuffleError } = validateAndParseShuffleRanges(formData.shuffleRanges);
    if (shuffleError) {
      setFormErrors({ shuffleRanges: shuffleError });
      return;
    }
    setIsSubmitting(true);
    try {
      if (testId === 'new') {
        const newTest = await addTest({ title: formData.title, description: formData.description, methodology: formData.methodology || null, shuffle_ranges: shuffleRangesParsed.length ? shuffleRangesParsed : null, color_mode: formData.colorMode, analysis_algorithm: formData.analysisAlgorithm || null, color_labels: formData.colorLabels.length ? formData.colorLabels : null, color_pair_index: formData.colorPairIndex });
        if (!newTest) throw new Error(t('editorErrorCreate'));
        navigate(`/test-editor/${newTest.id}`);
      } else {
        await updateTest(Number(testId), { title: formData.title, description: formData.description, methodology: formData.methodology || null, shuffle_ranges: shuffleRangesParsed.length ? shuffleRangesParsed : null, color_mode: formData.colorMode, analysis_algorithm: formData.analysisAlgorithm || null, color_labels: formData.colorLabels.length ? formData.colorLabels : null, color_pair_index: formData.colorPairIndex });
        navigate('/tests');
      }
    } catch {
      setToastMsg(t('editorErrorSave'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: '32px' }}>{t('genLoading')}</div>;

  const isNew = testId === 'new';

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>{isNew ? t('editorTitleNew') : t('editorTitleEdit')}</PageTitle>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button variant="outline" size="md" onClick={() => navigate('/tests')}>
            {t('cancelButton')}
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('genSaving') : isNew ? t('genCreate') : t('genSave')}
          </Button>
        </div>
      </PageHeader>

      {/* ── Restore draft banner ── */}
      {showRestorePrompt && (
        <RestoreBanner>
          <span>{t('editorRestoreMsg')}</span>
          <Button variant="primary" size="sm" onClick={handleRestore}>{t('editorRestore')}</Button>
          <Button variant="outline" size="sm" onClick={handleDiscardDraft}>{t('editorDiscard')}</Button>
        </RestoreBanner>
      )}

      {/* ── Test meta form ── */}
      <FormSection>
        <form onSubmit={handleSubmit} onKeyDown={preventImplicitSubmit}>
          <FormGroup>
            <label htmlFor="title">{t('editorLabelName')}</label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder={t('editorPlaceName')}
              maxLength={2000}
              error={!!formErrors.title}
              fullWidth
            />
            {formErrors.title && <ErrorText>{formErrors.title}</ErrorText>}
          </FormGroup>
          <FormGroup>
            <label htmlFor="description">{t('editorLabelDesc')}</label>
            <TextArea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('editorPlaceDesc')}
              maxLength={2000}
              fullWidth
            />
          </FormGroup>
          <FormGroup>
            <label htmlFor="methodology">{t('editorLabelMethodology')}</label>
            <Input
              id="methodology"
              name="methodology"
              value={formData.methodology}
              onChange={handleInputChange}
              placeholder={t('editorPlaceMethodology')}
              fullWidth
            />
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>{t('editorMethodologyHint')}</div>
          </FormGroup>
          <FormGroup>
            <label htmlFor="shuffleRanges">{t('editorLabelShuffle')}</label>
            <Input
              id="shuffleRanges"
              name="shuffleRanges"
              value={formData.shuffleRanges}
              onChange={handleInputChange}
              placeholder={t('editorPlaceShuffle')}
              error={!!formErrors.shuffleRanges}
              fullWidth
            />
            {formErrors.shuffleRanges
              ? <ErrorText>{formErrors.shuffleRanges}</ErrorText>
              : <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>{t('editorShuffleHint')}</div>
            }
          </FormGroup>
          <FormGroup>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={formData.colorMode}
                onChange={(e) => setFormData((prev) => ({ ...prev, colorMode: e.target.checked }))}
              />
              {t('editorColorModeLabel')}
            </label>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>{t('editorColorModeHint')}</div>
          </FormGroup>
          {formData.colorMode ? (
            <>
              {/* ── Названия цветовых меток ── */}
              <FormGroup>
                <label>{t('editorColorLabelsTitle')}</label>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0px', marginBottom: '8px' }}>
                  {t('editorColorLabelsHint')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px' }}>
                  {COLOR_OPTIONS.map((color, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          display: 'inline-block', width: 28, height: 28, borderRadius: '50%',
                          backgroundColor: color, border: '1px solid #ccc', flexShrink: 0,
                        }}
                      />
                      <Input
                        style={{ flex: 1 }}
                        placeholder={color}
                        value={formData.colorLabels?.[idx] ?? ''}
                        onChange={(e) => {
                          const next = [...(formData.colorLabels || [])];
                          next[idx] = e.target.value;
                          setFormData((prev) => ({ ...prev, colorLabels: next }));
                        }}
                      />
                    </div>
                  ))}
                </div>
              </FormGroup>

              {/* ── Матрица ИЦ ── */}
              <FormGroup>
                <label>{t('editorColorPairIndexTitle')}</label>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0px', marginBottom: '8px' }}>
                  {t('editorColorPairIndexHint')}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '4px 6px', border: '1px solid #ccc', background: '#f5f5f5', minWidth: 28 }}>↓1-й / 2-й→</th>
                        {Array.from({ length: 8 }, (_, ci) => (
                          <th key={ci} style={{ padding: '4px 6px', border: '1px solid #ccc', background: '#f5f5f5', minWidth: 48 }}>
                            <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', backgroundColor: COLOR_OPTIONS[ci], border: '1px solid #aaa' }} />
                            <span style={{ marginLeft: 4 }}>{ci + 1}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 8 }, (_, ri) => (
                        <tr key={ri}>
                          <td style={{ padding: '4px 6px', border: '1px solid #ccc', background: '#f5f5f5', textAlign: 'center' }}>
                            <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', backgroundColor: COLOR_OPTIONS[ri], border: '1px solid #aaa' }} />
                            <span style={{ marginLeft: 4 }}>{ri + 1}</span>
                          </td>
                          {Array.from({ length: 8 }, (_, ci) => {
                            const pairIdx = formData.colorPairIndex ?? DEFAULT_IC_TABLE;
                            const val = (pairIdx[ri]?.[ci] ?? DEFAULT_IC_TABLE[ri]?.[ci] ?? 0).toFixed(2);
                            return (
                              <td key={ci} style={{ padding: 0, border: '1px solid #ccc' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="1"
                                  style={{
                                    width: 56, padding: '4px 2px', textAlign: 'center', border: 'none',
                                    background: 'transparent', fontSize: '0.85rem',
                                  }}
                                  value={val}
                                  onChange={(e) => {
                                    const parsed = parseFloat(e.target.value);
                                    if (isNaN(parsed)) return;
                                    const prev = formData.colorPairIndex
                                      ? formData.colorPairIndex.map((r) => [...r])
                                      : DEFAULT_IC_TABLE.map((r) => [...r]);
                                    if (!prev[ri]) prev[ri] = Array(8).fill(0);
                                    prev[ri][ci] = Math.round(parsed * 100) / 100;
                                    setFormData((prevState) => ({ ...prevState, colorPairIndex: prev }));
                                  }}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </FormGroup>
            </>
          ) : (
            <FormGroup>
              <label>{t('editorLabelAlgorithm')}</label>
              <AlgorithmEditor
                value={formData.analysisAlgorithm}
                onChange={(val) => setFormData((prev) => ({ ...prev, analysisAlgorithm: val }))}
              />
            </FormGroup>
          )}
        </form>
      </FormSection>

      {/* ── Questions ── */}
      {currentTest && (
        <QuestionsSection>
          <QuestionsHeader>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700 }}>
              {t('editorStimuliSection')} ({questions.length})
            </h2>
          </QuestionsHeader>

          <QuestionsList>
            {questions.map((question, idx) => (
              <div
                key={question.id}
                ref={(el) => { questionRefs.current[question.id] = el; }}
                data-question-id={question.id}
              >
              <QuestionCard
                style={{ opacity: question.is_hidden ? 0.5 : 1 }}
              >
                <QuestionCardHeader>
                  <QuestionTitle>
                    {t('editorStimulusN')} {idx + 1}
                    {question.is_hidden && (
                      <span style={{ marginLeft: 8, fontSize: '0.8rem', color: '#ef4444', fontWeight: 500 }}>
                        {t('editorHiddenBadge')}
                      </span>
                    )}
                  </QuestionTitle>
                  <QuestionActions>
                    {/* Кегль шрифта стимула при прохождении теста (пусто = по умолчанию) */}
                    <FontSelect
                      value={question.font_size ?? ''}
                      title={t('editorFontTitle')}
                      onChange={(e) =>
                        updateLocalQuestion(question.id, {
                          font_size: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">{t('editorFontDefault')}</option>
                      {[8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72].map((pt) => (
                        <option key={pt} value={pt}>{pt}pt</option>
                      ))}
                    </FontSelect>
                    {/* Клики по ОтО до смены стимула (1 = обычный режим) */}
                    <FontSelect
                      value={question.clicks_to_advance ?? ''}
                      title={t('editorClicksTitle')}
                      onChange={(e) =>
                        updateLocalQuestion(question.id, {
                          clicks_to_advance: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">{t('editorClicksDefault')}</option>
                      {Array.from(
                        { length: Math.max(0, question.answer_options.length - 1) },
                        (_, i) => i + 2,
                      ).map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </FontSelect>
                    <DuplicateRow>
                      <SmallInput
                        type="number"
                        value={duplicateCounts[question.id] ?? 1}
                        min={1}
                        max={500}
                        onChange={(e) =>
                          setDuplicateCounts((prev) => ({
                            ...prev,
                            [question.id]: Math.max(1, Number(e.target.value)),
                          }))
                        }
                      />
                      <Button variant="outline" size="sm" onClick={() => handleDuplicateQuestion(question.id)}>
                        {t('editorBtnCopy')}
                      </Button>
                    </DuplicateRow>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem', color: '#555', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={(question.parameters?.justify_text || '') === 'true'}
                        onChange={(e) =>
                          updateLocalQuestion(question.id, {
                            parameters: { ...(question.parameters || {}), justify_text: e.target.checked ? 'true' : '' },
                          })
                        }
                      />
                      {t('editorJustifyLabel')}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem', color: '#555', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={question.is_hidden}
                        onChange={(e) => updateLocalQuestion(question.id, { is_hidden: e.target.checked })}
                      />
                      {t('editorHideLabel')}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem', color: '#555', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={question.allow_custom_answer ?? false}
                        onChange={(e) => updateLocalQuestion(question.id, { allow_custom_answer: e.target.checked })}
                      />
                      {t('editorCustomAnswerLabel')}
                    </label>
                    <Button variant="outline" size="sm" onClick={() => handleDeleteQuestion(question.id)}>
                      {t('editorBtnDelete')}
                    </Button>
                  </QuestionActions>
                </QuestionCardHeader>

                <FormGroup>
                  <label>{t('editorLabelStimulusText')}</label>
                  <TextArea
                    value={question.content}
                    onChange={(e) => updateLocalQuestion(question.id, { content: e.target.value })}
                    placeholder={t('editorPlaceStimulusText')}
                    maxLength={2000}
                    fullWidth
                  />
                </FormGroup>

                <FormGroup>
                  <label>{t('editorLabelStimulusMedia')}</label>
                  <Input
                    value={question.media_url ?? ''}
                    onChange={(e) =>
                      updateLocalQuestion(question.id, {
                        media_url: e.target.value.trim() || undefined,
                        type: e.target.value.trim() ? 'media' : 'text',
                      })
                    }
                    placeholder={t('editorPlaceStimulusMedia')}
                    fullWidth
                  />
                  <HintText>{t('editorStimulusMediaHint')}</HintText>
                  {question.media_url && (
                    <div style={{ marginTop: 8 }}>
                      {/\.(mp4|webm|ogg)$/i.test(question.media_url) ? (
                        <video controls muted preload="metadata" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6 }}>
                          <source src={question.media_url} type="video/mp4" />
                        </video>
                      ) : (
                        <img
                          src={question.media_url}
                          alt={t('editorLabelStimulusMedia')}
                          style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6 }}
                        />
                      )}
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <label>{t('editorLabelInfo')}</label>
                  <TextArea
                    value={question.info || ''}
                    onChange={(e) => updateLocalQuestion(question.id, { info: e.target.value })}
                    placeholder={t('editorPlaceInfo')}
                    maxLength={2000}
                    fullWidth
                  />
                </FormGroup>

                <FormGroup>
                  <label>{t('editorLabelExposition')}</label>
                  <Input
                    type="number"
                    min={0}
                    value={question.time_limit}
                    onChange={(e) => updateLocalQuestion(question.id, { time_limit: Number(e.target.value) })}
                    placeholder={t('editorPlaceNoLimit')}
                    style={{ width: '160px' }}
                  />
                  <HintText>{t('editorHintNoLimit')}</HintText>
                </FormGroup>

                <CollapsibleMeta title={t('editorMetaStimulus')}>
                  <ParameterEditor
                    title={t('editorMetaStimulus')}
                    parameters={recordToParams(question.parameters || {})}
                    onChange={(params) => updateLocalQuestion(question.id, { parameters: paramsToRecord(params) })}
                  />
                </CollapsibleMeta>

                {formData.colorMode && (
                  <FormGroup>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 500 }}>
                      <input
                        type="checkbox"
                        checked={(question.parameters?.hide_color_labels || '') === 'true'}
                        onChange={(e) =>
                          updateLocalQuestion(question.id, {
                            parameters: { ...(question.parameters || {}), hide_color_labels: e.target.checked ? 'true' : '' },
                          })
                        }
                      />
                      {t('editorHideColorLabels')}
                    </label>
                    <HintText>{t('editorHideColorLabelsHint')}</HintText>
                  </FormGroup>
                )}

                {!formData.colorMode && (
                  <FormGroup style={{ marginTop: '16px' }}>
                    <label>{t('editorAnswerFormLabel')}</label>
                    <select
                      value={(question.parameters?.answer_form || 'buttons') === 'scale' ? 'scale' : 'buttons'}
                      onChange={(e) => {
                        const form = e.target.value;
                        if (form === 'scale') {
                          // При переходе на шкалу очищаем варианты (ответ — число по шкале).
                          updateLocalQuestion(question.id, {
                            answer_options: [],
                            parameters: { ...(question.parameters || {}), answer_form: 'scale' },
                          });
                        } else {
                          // Возврат к кнопкам — ставим два варианта по умолчанию.
                          updateLocalQuestion(question.id, {
                            answer_options: [
                              { content: '', coefficient: 1, parameters: {} },
                              { content: '', coefficient: 0, parameters: {} },
                            ],
                            parameters: { ...(question.parameters || {}), answer_form: '' },
                          });
                        }
                      }}
                    >
                      <option value="buttons">{t('editorAnswerFormButtons')}</option>
                      <option value="scale">{t('editorAnswerFormScale')}</option>
                    </select>
                    <HintText>{t('editorAnswerFormScaleHint')}</HintText>

                    {(question.parameters?.answer_form || '') === 'scale' && (
                      <ScaleParamsEditor
                        params={question.parameters || {}}
                        onChange={(key, value) => updateQuestionParam(question.id, key, value)}
                      />
                    )}
                  </FormGroup>
                )}

                {!formData.colorMode && (question.parameters?.answer_form || '') !== 'scale' && <FormGroup style={{ marginTop: '16px' }}>
                  <label>{t('editorLabelOptions')}</label>
                  {question.answer_options.map((option, optIdx) => (
                    <div key={option.id !== undefined ? `id-${option.id}` : `new-${optIdx}`}>
                      <OptionRow>
                        <OptionLetter>{String.fromCharCode(65 + optIdx)}</OptionLetter>
                        <Input
                          value={option.content}
                          onChange={(e) => updateLocalOption(question.id, option.id, { content: e.target.value })}
                          placeholder={`${t('editorOptionPlaceholder')} ${optIdx + 1}`}
                          maxLength={2000}
                          style={{ flex: 1, minWidth: '120px' }}
                        />
                        <CoeffInput
                          type="number"
                          step="0.5"
                          value={option.coefficient}
                          onChange={(e) =>
                            updateLocalOption(question.id, option.id, { coefficient: Number(e.target.value) })
                          }
                          placeholder={t('editorOptionScore')}
                        />
                        <Input
                          value={option.media_url ?? ''}
                          onChange={(e) => updateLocalOption(question.id, option.id, { media_url: e.target.value })}
                          placeholder={t('editorOptionMediaPlaceholder')}
                          style={{ flex: 1, minWidth: '120px' }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeOptionFromQuestion(question.id, option.id)}
                          style={{ flexShrink: 0 }}
                        >
                          ×
                        </Button>
                      </OptionRow>
                      <div style={{ marginLeft: '40px' }}>
                        <ParameterEditor
                          title={`${t('editorMetaOptionPrefix')} ${String.fromCharCode(65 + optIdx)} ${t('editorMetaOptionSuffix')}`}
                          parameters={recordToParams(option.parameters || {})}
                          onChange={(params) =>
                            updateLocalOption(question.id, option.id, { parameters: paramsToRecord(params) })
                          }
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => addOptionToQuestion(question.id)}
                    style={{ marginTop: '8px' }}
                  >
                    {t('editorAddOption')}
                  </Button>
                </FormGroup>}
              </QuestionCard>
              </div>
            ))}
          </QuestionsList>
        </QuestionsSection>
      )}

      <ScrollTopButton $visible={showScrollTop} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title={t('scrollTopTitle')}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="18 15 12 9 6 15"/>
        </svg>
      </ScrollTopButton>

      {currentTest && (
        <FixedBottomBar>
          {/* Scrollable stimulus navigator */}
          <StimNavScrollArea ref={navScrollRef}>
            {questions.map((q, idx) => (
              <StimSquare
                key={q.id}
                $active={q.id === activeQuestionId}
                $hidden={q.is_hidden}
                $dragOver={dragOverSquareIdx === idx}
                draggable
                onClick={() => handleSquareClick(q.id)}
                onDragStart={(e) => handleSquareDragStart(e, idx)}
                onDragOver={(e) => handleSquareDragOver(e, idx)}
                onDrop={(e) => handleSquareDrop(e, idx)}
                onDragEnd={handleSquareDragEnd}
                title={[
                  `${t('editorStimulusN')} ${idx + 1}${q.is_hidden ? ` ${t('editorHiddenBadge')}` : ''}`,
                  q.content ? q.content.trim().split(/\s+/).slice(0, 5).join(' ') : '',
                ].filter(Boolean).join(': ')}
              >
                {idx + 1}
              </StimSquare>
            ))}
          </StimNavScrollArea>

          {/* Right side: add button + status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <Button variant="secondary" size="md" onClick={addNewQuestion}>
              {t('editorAddStimulus')}
            </Button>
            {!isOnline && (
              <span style={{ fontSize: '0.85rem', color: '#ef4444', fontWeight: 600 }}>
                {t('editorOffline')}
              </span>
            )}
            {isOnline && autoSaveStatus === 'saving' && (
              <span style={{ fontSize: '0.85rem', color: '#888' }}>{t('genSaving')}</span>
            )}
            {isOnline && autoSaveStatus === 'syncing' && (
              <span style={{ fontSize: '0.85rem', color: '#3b82f6', fontWeight: 600 }}>{t('editorSyncing')}</span>
            )}
            {isOnline && autoSaveStatus === 'saved' && (
              <span style={{ fontSize: '0.85rem', color: '#22c55e', fontWeight: 600 }}>{t('editorSaved')}</span>
            )}
            {autoSaveStatus === 'error' && (
              <span style={{ fontSize: '0.85rem', color: '#ef4444', fontWeight: 600 }}>{t('editorAutoSaveError')}</span>
            )}
          </div>
        </FixedBottomBar>
      )}

      {/* ── Toast ── */}
      {toastMsg && <ToastMsg>{toastMsg}</ToastMsg>}
    </PageContainer>
  );
};

export default EditorPage;
